import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useLang, useTr } from "@/i18n";

const LANG_OPTIONS = [
  { code: "it", path: "/", label: "IT" },
  { code: "en", path: "/en", label: "EN" },
  { code: "de", path: "/de", label: "DE" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const lang = useLang();
  const tr = useTr();
  const anchorPrefix = lang === "it" ? "/#" : "#";

  const links = [
    { href: `${anchorPrefix}servizi`, label: tr("nav.services", "Servizi"), testid: "nav-link-services" },
    { href: `${anchorPrefix}generi`, label: tr("nav.genres", "Generi"), testid: "nav-link-genres" },
    { href: `${anchorPrefix}esperienza`, label: tr("nav.experience", "Esperienza"), testid: "nav-link-experience" },
    { href: `${anchorPrefix}galleria`, label: tr("nav.gallery", "Galleria"), testid: "nav-link-gallery" },
    { href: `${anchorPrefix}contatti`, label: tr("nav.contact", "Contatti"), testid: "nav-link-contact" },
  ];

  const switcher = (mobile = false) => (
    <div
      data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"}
      className="flex items-center gap-1 rounded-full gold-frame px-1 py-0.5"
      role="navigation"
      aria-label="Language / Lingua / Sprache"
    >
      {LANG_OPTIONS.map((o) => (
        <a
          key={o.code}
          href={o.path}
          hrefLang={o.code}
          data-testid={`lang-switch-${o.code}${mobile ? "-mobile" : ""}`}
          aria-current={lang === o.code ? "true" : undefined}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] transition-colors ${
            lang === o.code
              ? "gold-btn"
              : "text-[#BFA45D] hover:text-[#F7DE8B]"
          }`}
        >
          {o.label}
        </a>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-[rgba(226,184,87,0.2)] bg-black/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href={LANG_OPTIONS.find((o) => o.code === lang)?.path || "/"} data-testid="nav-brand-logo" aria-label="DJ Magix — home" className="flex items-center gap-3">
          <img src="/assets/logo-256.png" alt="DJ Magix" width={40} height={40} className="h-10 w-10 object-contain" />
          <span className="font-display tracking-[0.25em] text-[#F7DE8B] text-sm font-semibold">
            DJ MAGIX
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              data-testid={l.testid}
              href={l.href}
              className="text-xs uppercase tracking-[0.2em] text-[#BFA45D] hover:text-[#F7DE8B] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to={lang === "en" ? "/en/blog" : "/blog"}
            data-testid="nav-link-blog"
            className="text-xs uppercase tracking-[0.2em] text-[#BFA45D] hover:text-[#F7DE8B] transition-colors"
          >
            Blog
          </Link>
          {switcher(false)}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="tel:+41764411359"
            data-testid="nav-phone-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full gold-frame px-4 py-2 text-xs tracking-widest text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
          >
            <Phone size={14} /> 076 441 13 59
          </a>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            className="md:hidden text-[#F7DE8B]"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-[rgba(226,184,87,0.2)] bg-black/95 px-6 py-5 flex flex-col gap-4">
          {links.map((l) => (
            <a
              key={l.href}
              data-testid={`${l.testid}-mobile`}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-[0.2em] text-[#BFA45D] hover:text-[#F7DE8B] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to={lang === "en" ? "/en/blog" : "/blog"}
            data-testid="nav-link-blog-mobile"
            onClick={() => setOpen(false)}
            className="text-sm uppercase tracking-[0.2em] text-[#BFA45D] hover:text-[#F7DE8B] transition-colors"
          >
            Blog
          </Link>
          {switcher(true)}
        </nav>
      )}
    </header>
  );
}
